import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
//CSS
import styles from "./NavbarItem.module.css";
import { NavbarItem } from "../../../Types/NavbarItem";
//Data
import { GetColour } from "../../../Data/ColourData";
import { navigateToLegacyMesma } from "../../../Data/NavbarData";
import { useAppSelector } from "../../../Redux/Store/Store";
import { getAiPowered, getClient } from "../../../Redux/Store/Slices/client";
import { getFeatureNotEnabledMessage } from "../../../Data/legacyNavMessages";
import Tooltip from "../../Utils/Tooltip/Tooltip";
import Popup from "../../Utils/Popup/Popup";

type NavbarItemProps = {
  item: NavbarItem;
  statusHandler: (id: number) => void;
  align?: "top" | "bottom";
  disabled?: boolean;
  disabledReason?: string;
};

const NavbarListItem = ({ item, statusHandler, align = "top", disabled = false, disabledReason }: NavbarItemProps) => {
  const navigate = useNavigate();
  const isAiPowered = useAppSelector(getAiPowered);
  const activeClient = useAppSelector(getClient);
  const isViewingResults = useAppSelector(state => state.questionnaire.isViewingResults);
  const isLegacyVersion = useAppSelector(state => state.questionnaire.questionnaire?.legacyVersion);

  const currentPath: string = window.location.pathname;

  const isCurrentPath = (title: string): boolean => {
    if (title.toLowerCase() === "dashboard") {
      return currentPath === "/";
    } else if (title.toLowerCase() === "portal") {
      return currentPath.startsWith("/template-editor") || currentPath.startsWith("/portal/");
    } else if (title.toLowerCase() === "track") {
      return currentPath.startsWith("/improve");
    } else {
      return currentPath.startsWith(`/${title.toLowerCase()}`);
    }
  };

  const isExternalNavLink =
    item.type === "navigation" && /^https?:\/\//i.test(item.url);
  const routeMatchesItem = isExternalNavLink ? false : isCurrentPath(item.title);

  const isAnalyseDisabled = item.title === "Analyse" && !isAiPowered;
  const [showVideoPopup, setShowVideoPopup] = useState<boolean>(false);

  const pageKeyFromPath = (path: string): string => {
    if (path === "/") return "dashboard";
    if (path.startsWith("/template-editor") || path.startsWith("/portal/")) return "portal";
    
    if (path.startsWith("/develop/active")) return "develop/active";
    if (path.startsWith("/develop/archive")) return "develop/archive";
    if (path.startsWith("/develop/")) {
      return isViewingResults ? "develop/summary" : "develop/diagnostic";
    }

    if (path.startsWith("/evaluate/active")) return "evaluate/active";
    if (path.startsWith("/evaluate/archive")) return "evaluate/archive";
    if (path.startsWith("/evaluate/")) {
      return isViewingResults ? "evaluate/summary" : "evaluate/diagnostic";
    }
    
    if (path.startsWith("/improve/active")) return "track/active";
    if (path.startsWith("/improve/")) return "track/plan";
    
    if (path.startsWith("/analyse")) return "analyse";
    
    if (path.startsWith("/admin/create-a-template") && path.includes("/define-structure")) return "admin/create-a-template/define-structure";
    if (path.startsWith("/admin/create-a-template") && path.includes("/define-questions")) return "admin/create-a-template/define-questions";
    if (path.startsWith("/admin/create-a-template") && path.includes("/additional-information")) return "admin/create-a-template/additional-information";
    if (path.startsWith("/admin/create-a-template")) return "admin/create-a-template/define-fields";
    if (path.startsWith("/admin/manage-templates")) return "admin/manage-templates";
    if (path.startsWith("/admin/mandate-templates")) return "admin/mandate-templates";

    return path.split("/")[1] || "default";
  };

  const hasVideoForPage = item.type === "video" && !!item.videos[pageKeyFromPath(currentPath)];
  const isVideoDisabled = item.type === "video" && !hasVideoForPage;
  const isItemDisabled = disabled || isAnalyseDisabled || isVideoDisabled;
  const disabledTooltip = disabled
    ? (disabledReason ?? getFeatureNotEnabledMessage(activeClient?.name))
    : isVideoDisabled
      ? "This page does not have video guidance yet."
      : "AI powered feature. Please upgrade to access.";

  return (
    <>
      {isItemDisabled ? (
        <Tooltip text={disabledTooltip} align="top" disabled={false}>
          <li
            className={`${styles.container} ${item.class} ${styles.disabled} ${align === "bottom" ? styles.bottom : ""}`}
            aria-disabled="true"
            onClick={() => {
              // Disabled - do nothing
            }}
            style={{
              backgroundColor:
                routeMatchesItem && item.title === "Evaluate"
                  ? GetColour(item.title)
                  : routeMatchesItem && item.title === "Enquire"
                  ? GetColour(item.title)
                  : routeMatchesItem && item.title === "Track"
                  ? GetColour("Improve")
                  : routeMatchesItem && item.title === "Develop"
                  ? GetColour(item.title)
                  : routeMatchesItem
                  ? "#F0F5F7"
                  : "",
                  color: routeMatchesItem ? "#24394F" : "",
                  opacity: 0.5,
                  cursor: 'not-allowed',
            }}
          >
            <img src={item.icon} alt={item.title} />
            <span>{item.title}</span>
          </li>
        </Tooltip>
      ) : (
        // TODO: Add text to explain that non-ai-powered users will not be able to interact with
        // ai-powered features
        <li
          className={`${styles.container} ${item.class} ${align === "bottom" ? styles.bottom : ""}`}
          onClick={() => {
            statusHandler(item.id);
            switch(item.type) {
              case "video":
                setShowVideoPopup(true);
                break;
              case "navigation":
                if (/^https?:\/\//i.test(item.url)) {
                  navigateToLegacyMesma(item.url);
                } else {
                  navigate(item.url);
                }
                break;
            }
          }}
          style={{
            backgroundColor:
              routeMatchesItem && item.title === "Evaluate"
                ? GetColour(item.title)
                : routeMatchesItem && item.title === "Enquire"
                ? GetColour(item.title)
                : routeMatchesItem && item.title === "Track"
                ? GetColour("Improve")
                : routeMatchesItem && item.title === "Develop"
                ? GetColour(item.title)
                : routeMatchesItem
                ? "#F0F5F7"
                : "",
                color: routeMatchesItem ? "#24394F" : "",
          }}
        >
          <img src={item.icon} alt={item.title} />
          <span>
              {item.title}
              {routeMatchesItem && item.title === "Analyse" ? <img src="/img/sparkle-blue.svg" className={styles.aiSparkleIcon} alt="AI Sparkle" />
               : item.title === "Analyse" ? <img src="/img/sparkle-grey.svg" className={styles.aiSparkleIcon} alt="AI Sparkle" /> : ''}
            </span>
        </li>
      )}
      {item.type === "video" && (
        <Popup
          isVisible={showVideoPopup}
          onClose={() => setShowVideoPopup(false)}
          style={{ width: "min(90vw, 960px)", maxHeight: "90vh" }}
        >
          {item.videos[pageKeyFromPath(currentPath)] && (
          <div style={{ justifyContent: "center" }}>
            <h1>{item.videos[pageKeyFromPath(currentPath)].title}</h1>
            <br/>
            {(!isAiPowered || isLegacyVersion) && (
              <p style={{ color: "#888", fontStyle: "italic", marginBottom: "1rem" }}>
                You are not currently using an AI-powered template. Some AI-powered features shown in this video may not be available to you.
              </p>
            )}
            <video
              src={item.videos[pageKeyFromPath(currentPath)].src}
              controls
              style={{ width: "100%", height: "auto", maxHeight: "80vh", border: "1px solid #d0d0d0", borderRadius: "8px" }}
            />
          </div>
          )}
          
        </Popup>
      )}
    </>
  );
};

export default NavbarListItem;
